import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Modal, Form, Button } from 'react-bootstrap';
import '../../pages/general/General.css';
import { fetchUserEvents, getUserEventById } from "../../service/EventsApi";
import { Loading, Breadcrumbs } from '../../components/shared';

const tamilDays = ['ஞாயி', 'திங்', 'செவ்', 'புதன்', 'வியா', 'வெள்', 'சனி'];

const UserCalendar = () => {
    const firstLoadRef = useRef(true);
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const calendarRef = useRef(null);
    const [eventRange, setEventRange] = useState({ min: null, max: null });
    const descriptionRef = useRef(null);
    const notesRef = useRef(null);
    const [bannerContent, setBannerContent] = useState('');

    useEffect(() => {
        loadEvents();
    }, []);

    useEffect(() => {
        const resizeTextArea = (ref) => {
            if (ref && ref.current) {
                ref.current.style.height = 'auto';
                ref.current.style.height = `${ref.current.scrollHeight}px`;
            }
        };
        resizeTextArea(descriptionRef);
        resizeTextArea(notesRef);
    }, [currentEvent?.description, currentEvent?.publicNotes]);

    // Inject dropdown after calendar renders
    useEffect(() => {
        const dropdownContainer = document.querySelector('.fc-viewDropdown-button');

        if (dropdownContainer && !dropdownContainer.querySelector('select')) {
            const select = document.createElement('select');
            select.style.padding = '6px 12px';
            select.style.borderRadius = '4px';
            select.style.border = '1px solid #ced4da';
            select.style.fontSize = '14px';

            const options = [
                { value: 'listMonth', label: 'பட்டியல்' },
                { value: 'dayGridMonth', label: 'மாதம்' },
                { value: 'timeGridWeek', label: 'வாரம்' },
                { value: 'timeGridDay', label: 'நாள்' },
            ];

            options.forEach(opt => {
                const option = document.createElement('option');
                option.value = opt.value;
                option.textContent = opt.label;
                select.appendChild(option);
            });

            select.addEventListener('change', (e) => {
                const view = e.target.value;
                calendarRef.current.getApi().changeView(view);
            });

            dropdownContainer.innerHTML = ''; // Clear default button text
            dropdownContainer.appendChild(select);
        }
    }, [events]); // run after events load, or you can tweak dependency as needed

    const parseDate = (dateValue) => {
        if (dateValue instanceof Date) return dateValue;
        if (dateValue?._seconds) return new Date(dateValue._seconds * 1000);
        if (typeof dateValue === 'string') return new Date(dateValue);
        return null;
    };

    const formatForInput = (date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        const hours = `${date.getHours()}`.padStart(2, '0');
        const minutes = `${date.getMinutes()}`.padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const loadEvents = async () => {
        setLoading(true);
        const result = await fetchUserEvents();

        if (Array.isArray(result.events)) {
            const formatted = result.events.map(event => {
                const start = parseDate(event.start);
                const end = parseDate(event.end);
                return {
                    id: event.id || event.docId || '',
                    title: event.title || '',
                    description: event.description || '',
                    publicNotes: event.publicNotes || '',
                    start,
                    end,
                };
            });

            setEvents(formatted);

            // ✅ Build banner content for next 30 days
            const now = new Date();
            const in30Days = new Date();
            in30Days.setDate(now.getDate() + 30);
            const bannerEvents = formatted
                .filter(e => e.publicNotes && e.end >= now && e.end <= in30Days)
                .map(e => `${e.title} - ${e.publicNotes}`);
            setBannerContent(bannerEvents.join(' | '));

            if (formatted.length > 0) {
                const starts = formatted.map(e => e.end.getTime());
                const ends = formatted.map(e => e.end.getTime());
                setEventRange({
                    min: new Date(Math.min(...starts, ...ends)),
                    max: new Date(Math.max(...starts, ...ends)),
                });
            }
        }

        setLoading(false);
    };
    const handleEventClick = async (arg) => {
        const eventId = arg.event.id;
        try {
            const eventData = await getUserEventById(eventId);
            if (eventData) {
                const start = parseDate(eventData.start);
                const end = parseDate(eventData.end);

                setCurrentEvent({
                    ...eventData,
                    start: formatForInput(start),
                    end: formatForInput(end),
                });
                setShowModal(true);
            } else {
                console.warn("No event data found for ID:", eventId);
            }
        } catch (err) {
            console.error("Error loading event details:", err);
        }
    };

    return (
        <>
            {loading ? (
                <div className="general_parent">
                    <div className="general_container">
                        <div className="general_body">
                            <Breadcrumbs /><br />
                            <Loading />
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="general_parent">
                        <div className="general_container">
                            <div className="general_body">
                                <Breadcrumbs /><br />
                                <h2>கோயில் நிகழ்வுகள்</h2>
                                {bannerContent && (
                                    <p >

                                        {bannerContent}

                                    </p>
                                )}

                                <div className="responsive-calendar">
                                    <FullCalendar
                                        ref={calendarRef}
                                        plugins={[
                                            dayGridPlugin,
                                            timeGridPlugin,
                                            interactionPlugin,
                                            listPlugin,
                                        ]}
                                        initialView="listMonth" // Default view is list for 1 month
                                        headerToolbar={{
                                            left: '',
                                            center: 'title',
                                            right: 'prev today next'
                                            // right: 'prev today viewDropdown next'
                                        }}
                                        customButtons={{
                                            viewDropdown: {
                                                text: '',
                                                click: () => { },
                                            },
                                        }}
                                        buttonText={{
                                            today: 'இன்று',
                                            month: 'மாதம்',
                                            week: 'வாரம்',
                                            day: 'நாள்',
                                            list: 'பட்டியல்',
                                        }}
                                        views={{
                                            listMonth: {
                                                type: 'list',
                                                duration: { months: 3 },
                                            },
                                        }}
                                        dayHeaderContent={(arg) => {
                                            const viewType = arg.view.type;
                                            const date = arg.date;
                                            const dayIndex = date.getDay();
                                            const tamilDay = tamilDays[dayIndex];

                                            if (viewType === 'dayGridMonth') {
                                                return `${tamilDay}`;
                                            } else if (viewType === 'timeGridWeek') {
                                                const day = date.getDate().toString().padStart(2, '0');
                                                return `${day}, ${tamilDay}`;
                                            }

                                            // For other views, optionally show more info
                                            const day = date.getDate().toString().padStart(2, '0');
                                            const year = date.getFullYear();
                                            const tamilMonth = new Intl.DateTimeFormat('ta-IN', { month: 'long' }).format(date);

                                            return (
                                                <div style={{ textAlign: 'center' }}>
                                                    <div>{`${day} ${tamilMonth} ${year}, ${tamilDay}`}</div>
                                                </div>
                                            );
                                        }}
                                        datesSet={(arg) => {
                                            const currentStart = new Date(arg.start);
                                            const currentEnd = new Date(arg.end);

                                            const prevButton = document.querySelector('.fc-prev-button');
                                            const nextButton = document.querySelector('.fc-next-button');

                                            if (eventRange.min && eventRange.max) {
                                                // Disable Prev if current view starts before the earliest event
                                                if (currentStart <= eventRange.min) {
                                                    prevButton?.setAttribute('disabled', 'disabled');
                                                    prevButton?.classList.add('fc-button-disabled');
                                                } else {
                                                    prevButton?.removeAttribute('disabled');
                                                    prevButton?.classList.remove('fc-button-disabled');
                                                }

                                                // Disable Next if current view ends after the latest event
                                                if (currentEnd >= eventRange.max) {
                                                    nextButton?.setAttribute('disabled', 'disabled');
                                                    nextButton?.classList.add('fc-button-disabled');
                                                } else {
                                                    nextButton?.removeAttribute('disabled');
                                                    nextButton?.classList.remove('fc-button-disabled');
                                                }
                                            }
                                            const titleElement = document.querySelector('.fc-toolbar-title');
                                            if (!titleElement) return;

                                            const tamilDateFormatter = new Intl.DateTimeFormat('ta-IN', { month: 'short', year: '2-digit' });

                                            const viewType = arg.view.type;
                                            const startDate = new Date(arg.start);
                                            const endDate = new Date(arg.end);

                                            function localMidnight(date) {
                                                return new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                            }

                                            const startDateLocal = localMidnight(startDate);
                                            const endDateLocal = localMidnight(new Date(endDate.getTime() - 1));
                                            let tamilTitle = '';
                                            if (firstLoadRef.current) {
                                                firstLoadRef.current = false;
                                            } else if (['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'list'].includes(viewType)) {
                                                // Compute dominant month
                                                const monthCount = {};
                                                const current = new Date(startDateLocal);
                                                while (current <= endDateLocal) {
                                                    const key = `${current.getFullYear()}-${current.getMonth()}`;
                                                    monthCount[key] = (monthCount[key] || 0) + 1;
                                                    current.setDate(current.getDate() + 1);
                                                }
                                                const dominantMonthKey = Object.keys(monthCount).reduce((a, b) => monthCount[a] > monthCount[b] ? a : b);
                                                const [year, month] = dominantMonthKey.split('-').map(Number);
                                                const dominantDate = new Date(year, month);

                                                tamilTitle = tamilDateFormatter.format(dominantDate);
                                            } else {
                                                tamilTitle = startDateLocal.getTime() === endDateLocal.getTime()
                                                    ? tamilDateFormatter.format(startDateLocal)
                                                    : `${tamilDateFormatter.format(startDateLocal)} – ${tamilDateFormatter.format(endDateLocal)}`;
                                            }
                                            titleElement.innerHTML = '';
                                            titleElement.appendChild(document.createTextNode(tamilTitle));
                                        }}
                                        events={events}
                                        eventClick={handleEventClick}
                                        selectable={false}
                                        editable={false}
                                        selectMirror={false}
                                        allDaySlot={false}
                                        dayMaxEvents={3}
                                        height="auto"
                                        nowIndicator={true}
                                    />
                                </div>

                                <Modal show={showModal} onHide={() => setShowModal(false)} >
                                    <Modal.Header closeButton>
                                        <Modal.Title>நிகழ்வு விவரங்கள்</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <Form>
                                            <Form.Group className="mb-3">
                                                <Form.Label>நிகழ்வு</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    readOnly
                                                    value={currentEvent?.title || ''}
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>விளக்கம்</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    readOnly
                                                    ref={descriptionRef}
                                                    value={currentEvent?.description || ''}
                                                    style={{ overflow: 'hidden', resize: 'none' }}
                                                    rows={1}
                                                />
                                            </Form.Group>

                                            {currentEvent?.publicNotes && (
                                                <Form.Group className="mb-3">
                                                    <Form.Label>குறிப்புகள்</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        readOnly
                                                        ref={notesRef}
                                                        value={currentEvent.publicNotes}
                                                        style={{ overflow: 'hidden', resize: 'none' }}
                                                        rows={1}
                                                    />
                                                </Form.Group>
                                            )}

                                            <Form.Group className="mb-3">
                                                <Form.Label>தொடக்கம்</Form.Label>
                                                <Form.Control
                                                    type="datetime-local"
                                                    readOnly
                                                    value={currentEvent?.start || ''}
                                                />
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Label>நிறைவு</Form.Label>
                                                <Form.Control
                                                    type="datetime-local"
                                                    readOnly
                                                    value={currentEvent?.end || ''}
                                                />
                                            </Form.Group>
                                        </Form>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                                            மூடு
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default UserCalendar;
